/**
 * useInactivityLock — Auto-locks screen after configurable inactivity timeout.
 *
 * DRR-183: Ambient display inactivity auto-lock for employee-only rooms.
 * Timer resets on mouse, keyboard, or touch events.
 * When locked, overlay shows facility branding + "Tap to unlock" prompt.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseInactivityLockReturn {
  isLocked: boolean;
  unlock: () => void;
  resetTimer: () => void;
}

export function useInactivityLock(timeoutMinutes: number = 15): UseInactivityLockReturn {
  const [isLocked, setIsLocked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsLocked(true);
    }, timeoutMinutes * 60 * 1000);
  }, [timeoutMinutes]);

  const unlock = useCallback(() => {
    setIsLocked(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'] as const;

    const handleActivity = () => {
      if (!isLocked) resetTimer();
    };

    for (const event of events) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    resetTimer();

    return () => {
      for (const event of events) {
        document.removeEventListener(event, handleActivity);
      }
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLocked, resetTimer]);

  return { isLocked, unlock, resetTimer };
}
